import {
  fetchCostsByStacks,
  fetchEnvironments,
  fetchStack,
  getStackType,
  getStackTypeTitle,
  selectEffectiveStackCost,
  selectEnvironmentsByStackId,
  selectStackById,
} from "@app/deploy";
import { findLoaderComposite } from "@app/loaders";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import {
  stackDetailEnvsUrl,
  stackDetailHidsUrl,
  stackDetailSettingsUrl,
  stackDetailUrl,
  stackDetailVpcPeeringsUrl,
  stackDetailVpnTunnelsUrl,
} from "@app/routes";
import { setResourceStats } from "@app/search";
import type { DeployStack } from "@app/types";
import { useEffect } from "react";
import { Outlet, useParams } from "react-router";
import {
  COST_ESTIMATE_TOOLTIP_TEXT,
  CopyText,
  type Crumb,
  DetailPageHeaderView,
  ResourceDetailBar,
  ResourceDetailItem,
  ResourceDetailsTable,
  type TabItem,
  Tooltip,
  getStackImg,
} from "../shared";
import { AppSidebarLayout } from "./app-sidebar-layout";

export function StackHeader({
  stack,
  isLoading,
  cost,
}: { stack: DeployStack; isLoading: boolean; cost: number | null }) {
  const stackType = getStackType(stack);

  return (
    <div className="flex flex-col gap-4">
      <ResourceDetailBar
        isLoading={isLoading}
        icon={<img alt="Stack icon" src={getStackImg(stackType)} />}
        nameLabel="Stack"
        name={stack.name}
        id={stack.id}
        createdAt={stack.createdAt}
        cost={cost}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <ResourceDetailsTable columns={2}>
        <ResourceDetailItem title="Tenancy">
          <Tooltip
            text={
              stackType === "shared"
                ? "For non-production use only. Does not support compliance frameworks or Aptible's SLA."
                : "For production workloads. Supports HIPAA and other compliance frameworks, includes VPN and VPC Peering."
            }
          >
            <span className="border-b border-dotted border-gray-400 cursor-default">
              {getStackTypeTitle(stack)}
            </span>
          </Tooltip>
        </ResourceDetailItem>
        {stackType !== "shared" ? (
          <ResourceDetailItem
            title="Network CIDR"
            titleTooltip="The internal network range (CIDR block) for this stack's VPC. Use this when configuring VPC peering, route tables, and security groups."
          >
            {stack.vpcCidr ? (
              <CopyText text={stack.vpcCidr} />
            ) : (
              "Not available"
            )}
          </ResourceDetailItem>
        ) : null}
        <ResourceDetailItem title="Region">{stack.region}</ResourceDetailItem>
        <ResourceDetailItem
          title="Outbound IP Addresses"
          titleTooltip="When sharing outbound IP addresses with vendors/partners for whitelisting, make sure to add all the provided IP addresses to the whitelist."
        >
          <CopyText text={stack.outboundIpAddresses.join(", ")} />
        </ResourceDetailItem>
      </ResourceDetailsTable>
    </div>
  );
}

export function StackPageHeader() {
  const { id = "" } = useParams();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setResourceStats({ id, type: "stack" }));
  }, []);
  const loaderStack = useQuery(fetchStack({ id }));
  const loaderEnvironments = useQuery(fetchEnvironments());
  const loader = findLoaderComposite([loaderStack, loaderEnvironments]);

  const stack = useSelector((s) => selectStackById(s, { id }));
  const environments = useSelector((s) =>
    selectEnvironmentsByStackId(s, { stackId: id }),
  );
  const crumbs: Crumb[] = [];
  const stackType = getStackType(stack);

  const tabs: TabItem[] = [
    {
      name: "Environments",
      href: stackDetailEnvsUrl(id),
      count: loader.isInitialLoading ? null : environments.length,
    },
  ];

  if (stackType !== "shared") {
    tabs.push(
      { name: "VPN Tunnels", href: stackDetailVpnTunnelsUrl(id) },
      { name: "VPC Peering", href: stackDetailVpcPeeringsUrl(id) },
    );
  }

  tabs.push({ name: "Settings", href: stackDetailSettingsUrl(id) });

  const cost = useSelector((s) => selectEffectiveStackCost(s, { id }));
  const { isLoading: isCostLoading } = useLoader(fetchCostsByStacks);

  if (stack.exposeIntrusionDetectionReports) {
    tabs.push({ name: "Managed HIDS", href: stackDetailHidsUrl(id) });
  }

  return (
    <DetailPageHeaderView
      {...loader}
      breadcrumbs={crumbs}
      titleLabel="Stack"
      title={stack.name}
      detailsBox={
        <StackHeader
          stack={stack}
          isLoading={loader.isLoading}
          cost={isCostLoading ? null : cost}
        />
      }
      tabs={tabs}
      lastBreadcrumbTo={stackDetailUrl(stack.id)}
    />
  );
}

export const StackDetailLayout = () => {
  return (
    <AppSidebarLayout header={<StackPageHeader />}>
      <Outlet />
    </AppSidebarLayout>
  );
};
